import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createSlug } from '../utils/createSlug';
import { normalizeRecipeSummaries } from '../utils/recipeSummaries';
import LoadingComponent from './LoadingComponent';
import api from '../api';
import Seo from '../Seo';
import './AllRecipes.css';

const recipePhotos = [
  'photo-1546833999-b9f581a1996d',
  'photo-1565557623262-b51c2513a641',
  'photo-1547592180-85f173990554',
  'photo-1540420773420-3366772f4999',
  'photo-1601050690597-df0568f70950',
  'photo-1512621776951-a57141f2eefd',
];

function AllRecipes() {
  // Use semantic links so search engines can discover recipe URLs
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('search') || '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [retryCount, setRetryCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(recipes.length / pageSize));
  const paginatedRecipes = recipes.slice((page - 1) * pageSize, page * pageSize);
  const recipeListJsonLd = recipes.length > 0
    ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Indian Recipes',
      itemListElement: paginatedRecipes.map((dish, index) => ({
        '@type': 'ListItem',
        position: (page - 1) * pageSize + index + 1,
        url: `https://indianfooddelight.com/recipes/${dish.slug || createSlug(dish.dishName)}`,
        name: dish.dishName,
      })),
    }
    : null;
  const hasLoadedRecipesRef = useRef(false);
  useEffect(() => {
    const searchQuery = searchParams.get('search') || '';
    if (searchQuery !== query) {
      setQuery(searchQuery);
      setPage(1);
    }
  }, [searchParams, query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchRecipes = async () => {
        const isInitialLoad = !hasLoadedRecipesRef.current;
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setListLoading(true);
        }

        setError(null);

        try {
          const endpoint = query.trim() ? '/recipe/suggestions' : '/recipe/all';
          const response = query.trim()
            ? await api.get(endpoint, { params: { typedName: query.trim() } })
            : await api.get(endpoint);
          setRecipes(normalizeRecipeSummaries(response.data));
        } catch (err) {
          console.error('Failed to load recipes:', err.response?.data || err.message);
          setError('Unable to load recipes. Please refresh or try again later.');
          setRecipes([]);
        } finally {
          if (isInitialLoad) {
            hasLoadedRecipesRef.current = true;
            setLoading(false);
          } else {
            setListLoading(false);
          }
        }
      };

      fetchRecipes();
    }, 300);

    return () => clearTimeout(timer);
  }, [query, retryCount]);

  // navigation handled by Link elements in the render tree

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setPage(1);
    setSearchParams(value ? { search: value } : {}, { replace: true });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handlePrevPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  return (
    <div className="content-panel-wide recipes-list-page">
      <Seo
        title="Indian Recipes | Find Your Next Favourite | Indian Food Delight"
        description="Browse Indian recipes, search for a dish, and discover something delicious to cook at home."
        path="/recipes"
        robots={query.trim() ? 'noindex,follow' : 'index,follow'}
      />
      {recipeListJsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(recipeListJsonLd).replace(/</g, '\\u003c'),
          }}
        />
      ) : null}
      {loading && recipes.length === 0 ? (
        <LoadingComponent />
      ) : (
        <>
          <section className="recipes-page-banner">
            <img
              src="https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=1400&q=80"
              alt="A table filled with colourful Indian curries and freshly cooked rice"
              width="1400"
              height="600"
            />
            <div className="recipes-page-banner-copy">
              <span className="eyebrow">A table full of inspiration</span>
              <h1>Find your next favourite.</h1>
              <p>Browse the collection, search a dish, and bring something delicious to your table.</p>
            </div>
          </section>

          <div className="section-header">
            <h2>All Available Recipes</h2>
            <p>Search by name or use the page controls to browse the full recipe catalog.</p>
          </div>

          <div className="recipe-controls">
            <div className="recipe-search-wrapper">
              <input
                type="search"
                className="recipe-search-input"
                value={query}
                onChange={handleSearchChange}
                placeholder="Search recipes..."
                aria-label="Search recipes by name"
              />
            </div>
            <div className="recipe-page-size">
              <label htmlFor="page-size-select">Show:</label>
              <select id="page-size-select" value={pageSize} onChange={handlePageSizeChange}>
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="alert-message alert-danger">
              <span>⚠️ {error}</span>
              <button
                type="button"
                className="recipe-retry-button"
                onClick={() => setRetryCount((count) => count + 1)}
              >
                Try again
              </button>
            </div>
          )}

          {recipes.length === 0 ? (error ? null : (
            <div className="empty-state-card">
              <h3>No recipes found</h3>
              <p>Try adjusting your search or changing the page size.</p>
            </div>
          )) : (
            <>
              <div className="all-recipes-grid">
                {paginatedRecipes.map((dish, index) => (
                    <Link
                      to={`/recipes/${dish.slug || createSlug(dish.dishName)}`}
                      key={`${dish.dishName}-${index}`}
                      className="recipe-link-item animate-fade-in-up"
                      aria-label={`View ${dish.dishName} recipe`}
                    >
                      <span className="recipe-card-visual">
                        <img
                          src={dish.imageURL || `https://images.unsplash.com/${recipePhotos[index % recipePhotos.length]}?auto=format&fit=crop&w=640&q=75`}
                          alt={dish.dishName}
                          width="640"
                          height="380"
                          loading="lazy"
                        />
                      </span>
                      <span className="recipe-card-details">
                        <span className="recipe-card-name">{dish.dishName}</span>
                        <span className="recipe-link-arrow">View recipe <span aria-hidden="true">→</span></span>
                      </span>
                    </Link>
                ))}
              </div>

              {listLoading && <div className="list-loading-overlay"><LoadingComponent /></div>}

              <div className="recipe-pagination">
                <button type="button" className="page-button" onClick={handlePrevPage} disabled={page === 1}>
                  Previous
                </button>
                <span className="page-summary">Page {page} of {totalPages}</span>
                <button type="button" className="page-button" onClick={handleNextPage} disabled={page >= totalPages}>
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AllRecipes;
